import { useId } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import HtmlText from '../components/HtmlText';
import { sanitizeSurveyHtml, surveyHtmlText } from '../sanitizeHtml';

/** Extensible service recap; empty notes never create an empty summary card. */
export function CompletedServiceSummary({ notes, title = "Completed service summary" }: { notes: (string | { body: string; title?: string })[]; title?: string }) {
  const id = useId();
  const unique = new Map<string, { html: string; title?: string }>();
  for (const note of notes) {
    const safe = sanitizeSurveyHtml(typeof note === "string" ? note : note.body);
    const text = surveyHtmlText(safe).replace(/\s+/g, ' ').trim();
    if (text && !unique.has(text)) unique.set(text, { html: safe, title: typeof note === "string" ? undefined : note.title });
  }
  if (!unique.size) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ bgcolor: 'var(--cx-brand-surface)', color: 'var(--cx-brand-text)', borderRadius: '18px !important', '&:before': { display: 'none' }, boxShadow: '0 8px 24px rgba(0,0,0,.06)' }}>
      <AccordionSummary id={`${id}-header`} aria-controls={`${id}-content`} expandIcon={<ExpandMoreRounded />} sx={{ minHeight: 64, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}>
        <Box sx={{ display: 'flex', p: 1, borderRadius: '50%', bgcolor: 'var(--cx-brand-primary)', color: 'white' }}><CheckRounded /></Box>
        <Box component="span" sx={{ fontWeight: 700 }}>{title}</Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'grid', gap: 1.5 }}>
        {[...unique].map(([key, note]) => <Box key={key}>
          {note.title && <HtmlText html={note.title} component="div" sx={{ fontWeight: 700, mb: 0.5 }} />}
          <HtmlText html={note.html} component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} />
        </Box>)}
      </AccordionDetails>
    </Accordion>
  );
}
